import { useCallback, useEffect, useRef, useState } from 'react';
import { lessonRunsApi } from '../../services/apiV2';
import { PacingTimerRuntimeState } from '../../utils/pacingTimer';
import { LessonResource } from '../../types/v2';

export interface ControlData {
  run: {
    id: number;
    lesson_plan: number;
    lesson_plan_title?: string;
    hook: number;
    section: number;
    section_name?: string;
    auto_makeup_for_absent_live_lessons?: boolean;
    started_by: number;
    started_at: string;
    ended_at: string | null;
    status: 'active' | 'paused' | 'completed';
    pacing_mode: 'whole_class' | 'selective';
    delivery_phase?: 'live' | 'studio';
    is_test_mode: boolean;
    reports_included?: boolean;
    reporting_exclusion_reason?: string | null;
    student_response_count?: number;
    real_student_response_count?: number;
    class_current_step: number | null;
    current_step_started_at?: string | null;
    current_step_timer?: PacingTimerRuntimeState | null;
    studio_started_at?: string | null;
    studio_entry_step?: number | null;
    studio_step_ids?: number[];
    live_resource: object;
    review_access?: 'off' | 'after_end' | 'always';
    allow_catchup?: boolean;
    show_assessment_answers_in_review?: boolean;
  };
  steps: Array<{
    id: number;
    order: number;
    title: string;
    stage_tag: string | null;
    runner_phase?: 'live' | 'studio';
    step_kind: string;
    counts_as_evidence: boolean;
    is_simulation_activity?: boolean;
    content?: any;
    signal_config?: any;
    evidence_config?: any;
    teacher_answer_key?: any;
    answer_key?: any;
  }>;
  students: Array<{
    id: number;
    name: string;
    joined: boolean;
    has_entered_live?: boolean;
    live_entered_at?: string | null;
    live_last_seen_at?: string | null;
    participation_status?: 'absent' | 'present_no_work' | 'participated';
    daily_attendance_status?: 'present' | 'absent' | null;
    effective_attendance_status?: 'present' | 'absent' | null;
    attendance_local_date?: string;
  }>;
  joined_student_ids: number[];
  entered_live_student_ids?: number[];
  live_presence_summary?: {
    total: number;
    entered_live: number;
    not_entered_live: number;
    absent: number;
    present_no_work: number;
    participated: number;
  };
  target_step_id: number | null;
  current_step_id?: number | null;
  current_step_teacher_answer_key?: any;
  display_step_id?: number | null;
  display_step_started_at?: string | null;
  display_step_teacher_answer_key?: any;
  resources?: LessonResource[];
}

export type TrackTargetScope =
  | 'needs_makeup'
  | 'all'
  | 'absent'
  | 'incomplete'
  | 'present_no_work'
  | 'participated'
  | 'custom';

export interface TrackAssignmentAudience {
  counts?: Record<string, number>;
  required_steps_total?: number;
  presence_summary?: {
    total?: number;
    entered_live?: number;
    not_entered_live?: number;
    absent?: number;
    present_no_work?: number;
    participated?: number;
  };
  buckets?: Record<string, number[]>;
}

export interface TrackAssignmentInfo {
  id: number;
  section: number;
  section_name?: string;
  lesson_plan: number;
  lesson_plan_title?: string;
  due_at?: string | null;
  delivery_mode?: string;
}

export interface LiveSignalStats {
  total_students: number;
  step_signal_submitted: number;
  step_signal_pending: number;
  missing_reasoning: number;
  missing_confidence: number;
  has_correct_answer: boolean;
  correct_count?: number;
  incorrect_count?: number;
  unscored_count?: number;
  high_confidence_wrong: number;
  low_confidence_right: number;
  low_confidence_wrong: number;
  bucket_student_ids?: Record<string, number[]>;
}

export interface LiveDashboardStudentRow {
  student_id: number;
  student_name: string;
  current_step_id: number | null;
  current_step_title: string;
  is_on_class_step: boolean;
  has_run_state: boolean;
  has_response: boolean;
  latest_signal_id: number | null;
  latest_signal_at: string | null;
  response_summary?: Record<string, any> | null;
  is_simulation_activity?: boolean;
  simulation_summary?: {
    status: string;
    phase: string | null;
    action_count: number;
    evidence_count: number;
    prompting_level: number;
    safety_flag: boolean;
    verification_status: string | null;
    repeated_blocked_actions: boolean;
    support_used: boolean;
    help_reason: string | null;
  } | null;
  presence: {
    has_entered_live: boolean;
    live_entered_at: string | null;
    live_last_seen_at: string | null;
    seconds_since_seen: number | null;
    recently_active: boolean;
    inactive: boolean;
  };
  flags: string[];
  primary_bucket: string;
}

export interface LiveDashboardPayload {
  run_id: number;
  status: string;
  current_step: {
    id: number;
    title: string;
    order: number;
    stage_tag: string | null;
    step_kind: string;
    runner_phase: string;
    live_response_mode: string;
    is_simulation_activity?: boolean;
  } | null;
  bucket_counts: Record<string, number>;
  buckets: Record<string, LiveDashboardStudentRow[]>;
  suggested_priority_order: string[];
  meta?: Record<string, any>;
}

interface UseLessonControlDataParams {
  runId?: string;
  normalizeStudentIds: (raw: any[]) => number[];
  enableTrackAssignment?: boolean;
  enableLiveSignalStats?: boolean;
}

const parsePollingMs = (rawValue: string | undefined, fallback: number): number => {
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(1000, Math.floor(parsed));
};

const LESSON_CONTROL_SYNC_POLL_MS = parsePollingMs(
  process.env.REACT_APP_LESSON_CONTROL_SYNC_POLL_MS,
  2000,
);
const LESSON_CONTROL_LIVE_SIGNAL_POLL_MS = parsePollingMs(
  process.env.REACT_APP_LESSON_CONTROL_LIVE_SIGNAL_POLL_MS,
  1500,
);

export const useLessonControlData = ({
  runId,
  normalizeStudentIds,
  enableTrackAssignment = true,
  enableLiveSignalStats = true,
}: UseLessonControlDataParams) => {
  const [controlData, setControlData] = useState<ControlData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRunContext, setLastRunContext] = useState<{section: number; lesson_plan: number; is_test_mode?: boolean} | null>(null);
  const [trackAssignment, setTrackAssignment] = useState(false);
  const [trackDueAt, setTrackDueAt] = useState('');
  const [trackTargetScope, setTrackTargetScope] = useState<TrackTargetScope>('needs_makeup');
  const [trackTargetStudentIds, setTrackTargetStudentIds] = useState<number[]>([]);
  const [trackAudience, setTrackAudience] = useState<TrackAssignmentAudience | null>(null);
  const [trackAssignmentInfo, setTrackAssignmentInfo] = useState<TrackAssignmentInfo | null>(null);
  const [trackError, setTrackError] = useState<string | null>(null);
  const [trackSummary, setTrackSummary] = useState<{
    total: number;
    completed: number;
    in_progress: number;
    not_started: number;
    excused: number;
  } | null>(null);
  const [liveSignalStats, setLiveSignalStats] = useState<LiveSignalStats | null>(null);
  const liveSignalRequestInFlightRef = useRef(false);
  const [liveDashboard, setLiveDashboard] = useState<LiveDashboardPayload | null>(null);
  const [liveDashboardLoading, setLiveDashboardLoading] = useState(false);
  const [liveDashboardError, setLiveDashboardError] = useState<string | null>(null);
  const liveDashboardRequestInFlightRef = useRef(false);

  const loadControl = useCallback(async (options?: { silent?: boolean }) => {
    if (!runId) return;
    const silent = Boolean(options?.silent);
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await lessonRunsApi.control(Number(runId));
      setControlData(response.data);
      const context = {
        section: response.data.run.section,
        lesson_plan: response.data.run.lesson_plan,
        is_test_mode: Boolean(response.data.run.is_test_mode),
      };
      setLastRunContext(context);
      localStorage.setItem(`lessonRunContext:${runId}`, JSON.stringify(context));
      setError(null);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setControlData(null);
        const storedContext = localStorage.getItem(`lessonRunContext:${runId}`);
        if (storedContext) {
          try {
            const context = JSON.parse(storedContext);
            setLastRunContext(context);
          } catch {
            setLastRunContext(null);
          }
        }
        setError('not_found');
      } else {
        if (!silent) {
          setControlData(null);
          setError('Failed to load lesson control data');
          // eslint-disable-next-line no-console
          console.error(err);
        }
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [runId]);

  useEffect(() => {
    void loadControl();
  }, [loadControl]);

  const loadTrackAssignment = useCallback(async () => {
    if (!controlData) return;
    try {
      const response = await lessonRunsApi.getTrackAssignment(controlData.run.id);
      setTrackAssignment(Boolean(response?.track));
      setTrackSummary(response?.summary || null);
      setTrackAudience(response?.audience || null);
      const assignmentPayload = response?.assignment && typeof response.assignment === 'object'
        ? response.assignment
        : null;
      const assignmentId = Number(assignmentPayload?.id || 0);
      if (Number.isInteger(assignmentId) && assignmentId > 0) {
        setTrackAssignmentInfo({
          id: assignmentId,
          section: Number(assignmentPayload?.section || 0),
          section_name: String(assignmentPayload?.section_name || '').trim() || undefined,
          lesson_plan: Number(assignmentPayload?.lesson_plan || 0),
          lesson_plan_title: String(assignmentPayload?.lesson_plan_title || '').trim() || undefined,
          due_at: assignmentPayload?.due_at || null,
          delivery_mode: assignmentPayload?.delivery_mode || undefined,
        });
      } else {
        setTrackAssignmentInfo(null);
      }
      const targetIdsRaw = Array.isArray(response?.target_student_ids) ? response.target_student_ids : [];
      setTrackTargetStudentIds(normalizeStudentIds(targetIdsRaw));
      const scope = String(response?.target_scope || '').trim().toLowerCase();
      if (
        scope === 'needs_makeup'
        || scope === 'all'
        || scope === 'absent'
        || scope === 'incomplete'
        || scope === 'present_no_work'
        || scope === 'participated'
        || scope === 'custom'
      ) {
        setTrackTargetScope(scope as TrackTargetScope);
      } else {
        setTrackTargetScope('needs_makeup');
      }
      const dueAt = response?.assignment?.due_at;
      if (dueAt) {
        const parsed = new Date(dueAt);
        if (!Number.isNaN(parsed.getTime())) {
          const offset = parsed.getTimezoneOffset() * 60000;
          setTrackDueAt(new Date(parsed.getTime() - offset).toISOString().slice(0, 16));
        }
      } else {
        setTrackDueAt('');
      }
      setTrackError(null);
    } catch (err: any) {
      setTrackAssignmentInfo(null);
      setTrackError(err?.response?.data?.error || 'Failed to load assignment tracking');
    }
  }, [controlData, normalizeStudentIds]);

  useEffect(() => {
    if (!enableTrackAssignment) return;
    void loadTrackAssignment();
  }, [enableTrackAssignment, loadTrackAssignment]);

  const loadLiveSignalStats = useCallback(async () => {
    if (!enableLiveSignalStats) return;
    if (!runId || liveSignalRequestInFlightRef.current) return;
    liveSignalRequestInFlightRef.current = true;
    try {
      const response = await fetch(`/api/v2/lesson-runs/${runId}/stats/`, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (!response.ok) return;
      const data = await response.json();
      setLiveSignalStats(data?.live_signals ?? null);
    } catch {
      // ignore polling errors
    } finally {
      liveSignalRequestInFlightRef.current = false;
    }
  }, [enableLiveSignalStats, runId]);

  const loadLiveDashboard = useCallback(async (options?: { silent?: boolean }) => {
    if (!runId || liveDashboardRequestInFlightRef.current) return;
    const silent = Boolean(options?.silent);
    liveDashboardRequestInFlightRef.current = true;
    try {
      if (!silent) {
        setLiveDashboardLoading(true);
      }
      const response = await fetch(`/api/v2/lesson-runs/${runId}/live-dashboard/`, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (!response.ok) {
        if (!silent) {
          setLiveDashboardError('Failed to load live dashboard');
        }
        return;
      }
      const data = await response.json();
      setLiveDashboard(data ?? null);
      setLiveDashboardError(null);
    } catch {
      if (!silent) {
        setLiveDashboardError('Failed to load live dashboard');
      }
    } finally {
      liveDashboardRequestInFlightRef.current = false;
      if (!silent) {
        setLiveDashboardLoading(false);
      }
    }
  }, [runId]);

  useEffect(() => {
    if (!runId || controlData?.run.status !== 'active') return;
    const interval = window.setInterval(() => {
      void loadControl({ silent: true });
      void loadLiveDashboard({ silent: true });
    }, LESSON_CONTROL_SYNC_POLL_MS);
    return () => window.clearInterval(interval);
  }, [controlData?.run.status, loadControl, loadLiveDashboard, runId]);

  useEffect(() => {
    void loadLiveDashboard();
  }, [loadLiveDashboard]);

  useEffect(() => {
    if (!enableLiveSignalStats) return;
    void loadLiveSignalStats();
  }, [enableLiveSignalStats, loadLiveSignalStats]);

  useEffect(() => {
    if (!enableLiveSignalStats) return;
    if (controlData?.run.status !== 'active') return;
    const interval = window.setInterval(() => {
      void loadLiveSignalStats();
    }, LESSON_CONTROL_LIVE_SIGNAL_POLL_MS);
    return () => window.clearInterval(interval);
  }, [controlData?.run.status, enableLiveSignalStats, loadLiveSignalStats]);

  useEffect(() => {
    if (!runId) return;
    const refreshWhenVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void loadControl({ silent: true });
      void loadLiveDashboard({ silent: true });
      if (enableLiveSignalStats) {
        void loadLiveSignalStats();
      }
    };
    const refreshOnFocus = () => {
      void loadControl({ silent: true });
      void loadLiveDashboard({ silent: true });
      if (enableLiveSignalStats) {
        void loadLiveSignalStats();
      }
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, [enableLiveSignalStats, loadControl, loadLiveDashboard, loadLiveSignalStats, runId]);

  return {
    controlData,
    setControlData,
    loading,
    error,
    lastRunContext,
    setLastRunContext,
    loadControl,
    trackAssignment,
    setTrackAssignment,
    trackDueAt,
    setTrackDueAt,
    trackTargetScope,
    setTrackTargetScope,
    trackTargetStudentIds,
    setTrackTargetStudentIds,
    trackAudience,
    setTrackAudience,
    trackAssignmentInfo,
    setTrackAssignmentInfo,
    trackError,
    setTrackError,
    trackSummary,
    setTrackSummary,
    loadTrackAssignment,
    liveSignalStats,
    setLiveSignalStats,
    loadLiveSignalStats,
    liveDashboard,
    liveDashboardLoading,
    liveDashboardError,
    loadLiveDashboard,
  };
};
